import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useEMDRAnimation } from '../../hooks/useEMDRAnimation'
import { useAudioEngine } from '../../hooks/useAudioEngine'
import { useJoyConManager } from '../../hooks/useJoyConManager'
import { useGamepadVibration } from '../../hooks/useGamepadVibration'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { useSubscription } from '../../hooks/useSubscription'
import EMDRBall from './EMDRBall'
import ControlPanel from './ControlPanel'
import './EMDRApp.css'

const EMDRApp = () => {
  const { user, signOut } = useAuth()
  const { initAudio, playBeep } = useAudioEngine()
  const { hasAccess } = useSubscription()
  
  // Joy-Con management
  const {
    joyConState,
    connectJoyCons,
    triggerJoyConVibration,
    updateJoyConEnabled,
    updateJoyConIntensity
  } = useJoyConManager()

  // Gamepad management (8BitDo USB)
  const {
    gamepadState,
    triggerVibration: triggerGamepadVibration,
    assignGamepadSide,
    updateEnabled: updateGamepadEnabled,
    updateIntensity: updateGamepadIntensity,
    scanGamepads,
    testVibration: testGamepadVibration
  } = useGamepadVibration()

  // Mode de vibration ('joycon' ou 'gamepad')
  // Sur Windows, on active le mode Gamepad (8BitDo) par défaut
  const [vibrationMode, setVibrationMode] = useState(() => {
    const isWindows = typeof navigator !== 'undefined' && 
      (navigator.platform?.indexOf('Win') > -1 || navigator.userAgent?.indexOf('Windows') > -1)
    return isWindows ? 'gamepad' : 'joycon'
  })

  // État de l'application
  const [isRunning, setIsRunning] = useState(false)
  const [frequency, setFrequency] = useState(0.8)
  const [verticalPosition, setVerticalPosition] = useState(0)
  const [backgroundColor, setBackgroundColor] = useState('#000000')
  const [ballColor, setBallColor] = useState('#0066ff') // Couleur par défaut bleue
  const [ballShape, setBallShape] = useState('circle') // Forme par défaut : cercle
  const [ballSize, setBallSize] = useState(1) // Taille par défaut : 1 (normale)
  const [volume, setVolume] = useState(0.5)
  const [soundType, setSoundType] = useState('soft-tone')
  const [soundFrequency, setSoundFrequency] = useState(5)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isControlsVisible, setIsControlsVisible] = useState(true)

  // Fonction de vibration combinée (utilise le mode actif)
  const triggerVibration = useCallback((side, duration) => {
    if (vibrationMode === 'joycon') {
      triggerJoyConVibration(side, duration)
    } else {
      triggerGamepadVibration(side, duration)
    }
  }, [vibrationMode, triggerJoyConVibration, triggerGamepadVibration])

  // Gestion du changement de direction pour le son
  const handleDirectionChange = useCallback((direction) => {
    if (isRunning) {
      const side = direction > 0 ? 'right' : 'left'
      playBeep(side, soundType, soundFrequency, volume, triggerVibration, frequency)
    }
  }, [isRunning, playBeep, soundType, soundFrequency, volume, triggerVibration, frequency])

  // Hook d'animation
  const { ballRef, resetBallPosition } = useEMDRAnimation({
    isRunning,
    frequency,
    verticalPosition,
    ballSize,
    onDirectionChange: handleDirectionChange
  })

  // Initialisation de l'audio au premier clic
  useEffect(() => {
    const handleFirstUserInteraction = () => {
      initAudio()
      document.removeEventListener('click', handleFirstUserInteraction)
      document.removeEventListener('touchstart', handleFirstUserInteraction)
    }

    document.addEventListener('click', handleFirstUserInteraction)
    document.addEventListener('touchstart', handleFirstUserInteraction)

    return () => {
      document.removeEventListener('click', handleFirstUserInteraction)
      document.removeEventListener('touchstart', handleFirstUserInteraction)
    }
  }, [initAudio])

  // Gestion du plein écran
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }, [])

  // Écouter les changements de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Force la couleur de fond sur le body et le html
  useEffect(() => {
    // Stocker les styles originaux
    const originalBodyBg = document.body.style.background
    const originalBodyBgColor = document.body.style.backgroundColor
    const originalHtmlBg = document.documentElement.style.background
    const originalHtmlBgColor = document.documentElement.style.backgroundColor
    const originalAppBg = document.querySelector('.emdr-app')?.style.background
    
    // Forcer la couleur de fond avec !important sur tous les éléments
    const styleElement = document.createElement('style')
    styleElement.id = 'emdr-background-override'
    styleElement.innerHTML = `
      body, html, #root, .emdr-app, .ball-container {
        background-color: ${backgroundColor} !important;
        background: ${backgroundColor} !important;
      }
    `
    document.head.appendChild(styleElement)
    
    // Nettoyer au démontage du composant
    return () => {
      const styleEl = document.getElementById('emdr-background-override')
      if (styleEl) {
        document.head.removeChild(styleEl)
      }
      // Restaurer les styles originaux
      document.body.style.background = originalBodyBg
      document.body.style.backgroundColor = originalBodyBgColor
      document.documentElement.style.background = originalHtmlBg
      document.documentElement.style.backgroundColor = originalHtmlBgColor
    }
  }, [backgroundColor])

  // Contrôles de session
  const startSession = useCallback(() => {
    setIsRunning(true) // Accès libre pour tous
  }, [])
  
  const pauseSession = useCallback(() => setIsRunning(false), [])
  
  const resetSession = useCallback(() => {
    setIsRunning(false)
    setFrequency(0.8)
    setVerticalPosition(0)
    resetBallPosition()
  }, [resetBallPosition])

  // Hook pour les raccourcis clavier
  const { shortcuts } = useKeyboardShortcuts({
    isRunning,
    frequency,
    verticalPosition,
    onStart: startSession,
    onPause: pauseSession,
    onReset: resetSession,
    onToggleFullscreen: toggleFullscreen,
    onFrequencyChange: setFrequency,
    onVerticalPositionChange: setVerticalPosition,
    minFrequency: 0.2,
    maxFrequency: 2.0,
    frequencyStep: 0.1,
    maxVerticalPosition: 400,
    verticalStep: 10
  })

  // Gestion du clic en dehors du panneau de contrôle
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ne pas fermer le panneau si on clique sur :
      // - Le panneau de contrôle lui-même
      // - Le trigger des contrôles
      // - La page d'abonnement (overlay)
      // - Les liens légaux de la page d'abonnement
      if (isControlsVisible && 
          !event.target.closest('.control-panel') && 
          !event.target.closest('.controls-trigger') &&
          !event.target.closest('.subscription-page-overlay') &&
          !event.target.closest('.subscription-legal-link')) {
        setIsControlsVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isControlsVisible])

  return (
    <div 
      className="emdr-app"
      style={{ backgroundColor }}
    >
      {/* Zone d'animation */}
      <div className="ball-container">
        <EMDRBall ref={ballRef} ballColor={ballColor} shape={ballShape} size={ballSize} />
      </div>

      {/* Trigger pour afficher les contrôles */}
      <div 
        className="controls-trigger"
        onClick={() => setIsControlsVisible(!isControlsVisible)}
      />

      {/* Panneau de contrôles */}
      <ControlPanel
        isVisible={isControlsVisible}
        isRunning={isRunning}
        frequency={frequency}
        verticalPosition={verticalPosition}
        backgroundColor={backgroundColor}
        ballColor={ballColor}
        ballShape={ballShape}
        ballSize={ballSize}
        volume={volume}
        soundType={soundType}
        soundFrequency={soundFrequency}
        isFullscreen={isFullscreen}
        user={user}
        shortcuts={shortcuts}
        onStart={startSession}
        onPause={pauseSession}
        onReset={resetSession}
        onFrequencyChange={setFrequency}
        onVerticalPositionChange={setVerticalPosition}
        onBackgroundColorChange={setBackgroundColor}
        onBallColorChange={setBallColor}
        onBallShapeChange={setBallShape}
        onBallSizeChange={setBallSize}
        onVolumeChange={setVolume}
        onSoundTypeChange={setSoundType}
        onSoundFrequencyChange={setSoundFrequency}
        onToggleFullscreen={toggleFullscreen}
        onSignOut={signOut}
        // Props Joy-Con
        joyConState={joyConState}
        onJoyConConnect={connectJoyCons}
        onJoyConEnabledChange={updateJoyConEnabled}
        onJoyConIntensityChange={updateJoyConIntensity}
        // Props Gamepad (8BitDo)
        gamepadState={gamepadState}
        onGamepadEnabledChange={updateGamepadEnabled}
        onGamepadIntensityChange={updateGamepadIntensity}
        onGamepadAssignSide={assignGamepadSide}
        onGamepadScan={scanGamepads}
        onGamepadTestVibration={testGamepadVibration}
        // Mode de vibration partage
        vibrationMode={vibrationMode}
        onVibrationModeChange={setVibrationMode}
      />
    </div>
  )
}

export default EMDRApp 